/**
 * termTotal.ts — back-compute a per-month price from a typed TERM TOTAL.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the admin discounted-term pricing config (PlanManagement's edit drawer)
 * lets an admin type either a per-month pence figure OR the term's total pence
 * — typing the total should back-compute the monthly price. Money is integer
 * pence everywhere, so a total only ever back-computes cleanly when it divides
 * exactly by the term's month count; anything else would silently invent a
 * rounding. Rather than reject with no way forward, this returns the two
 * NEAREST totals that DO divide exactly (one at-or-below, one above) so the
 * form can suggest them ("must divide by N — try £A or £B").
 */

export type TermTotalResult =
  | { ok: true; monthlyPence: number }
  | { ok: false; message: string; nearestBelow?: number; nearestAbove?: number };

/**
 * Given a typed term-total (integer pence) and the term's month count, return
 * the back-computed monthly pence, or — when the total does not divide
 * exactly — the two nearest clean totals (in pence) either side of it.
 */
export function monthlyFromTermTotal(totalPence: number, months: number): TermTotalResult {
  if (!Number.isInteger(months) || months < 1) {
    return { ok: false, message: 'Invalid term length.' };
  }
  if (!Number.isFinite(totalPence) || !Number.isInteger(totalPence) || totalPence < 0) {
    return { ok: false, message: 'Enter a whole number of pence, 0 or more.' };
  }
  if (totalPence % months === 0) {
    return { ok: true, monthlyPence: totalPence / months };
  }
  const nearestBelow = Math.floor(totalPence / months) * months;
  const nearestAbove = nearestBelow + months;
  return {
    ok: false,
    message: `Must divide by ${months} into whole pence.`,
    nearestBelow,
    nearestAbove,
  };
}
